import React, { useState, useMemo, useEffect, useContext, useRef } from 'react';
import { themes } from '../styles/themes';
import { useTranslate } from './languageContext';
import { usePage } from './pageContext';
import useIni from '../hooks/useIni';
import useMeasure from 'use-measure';
import Cargo from '../components/svg/Cargo';
import { addDot } from '../utils';

const MapContext = React.createContext({});

export const MapProvider = ({ children }) => {
  const t = useTranslate();
  const mapRef = useRef(null);
  const mapDomRef = useRef(null);
  const tooltipRef = useRef(null);
  const mapMeasure = useMeasure(mapDomRef);
  const { showCurules } = useIni();
  const [selectedMap, setSelectedMap] = useState({});
  const [tooltipProps, setTooltipProps] = useState({});
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const {
    urlInfo: { page },
  } = usePage();

  const maps = useMemo(
    () =>
      [
        {
          variant: 'inicio',
          valueFormatter: el => {
            return {
              value: Number(el.pmesesc.replace(/,/g, '.').slice(0, -1)),
              showValue: () => (
                <>
                  <span className="title">Mesas escrutadas: </span>
                  <span className="data">
                    {el.pmesesc}{' '}
                    <i>
                      ({addDot(el.mesesc)} de {addDot(el.metota)})
                    </i>
                  </span>
                </>
              ),
            };
          },
          label: t('mesasInf'),
          id: 1,
          theme: themes.mesasInformadas,
          tooltip: 'Mesas informadas: ',
        },
        showCurules
          ? {
              variant: 'resultados',
              label: t('repartoCurules'),
              id: 2,
              valueFormatter: el => {
                const hayEmpate = el.hayEmpate === '1' && el.mesesc !== '0';
                return {
                  codpar: el.codpar,
                  vot: el.vot,
                  showValue: () => (
                    <>
                      <span className="title">Cargos: </span>
                      <span className="data">
                        {addDot(el.carg)} <Cargo /> ({el.pvot},{' '}
                        <i>{addDot(el.vot)} votos)</i>
                      </span>
                    </>
                  ),
                  mesas: el.mesesc,
                  hayEmpate,
                };
              },
              tiePartiesCalc: data =>
                data
                  .filter(({ act }) => act.carg === data[0].act.carg)
                  .map(({ act }) => act),
            }
          : {},
        {
          variant: 'resultados',
          label: t('partidosMasVotados'),
          valueFormatter: el => {
            const hayEmpate = el.hayEmpate === '1' && el.mesesc !== '0';
            return {
              codpar: el.codpar,
              vot: el.vot,
              showValue: () => (
                <>
                  <span className="title">Votos:</span>
                  <span className="data">
                    {el.pvot} <i>({addDot(el.vot)} votos)</i>
                  </span>
                </>
              ),
              mesas: el.mesesc,
              hayEmpate,
            };
          },
          tiePartiesCalc: data =>
            data
              .filter(({ act }) => act.pvot === data[0].act.pvot)
              .map(({ act }) => act),
          id: 3,
          theme: themes.sectores,
          tooltip: '',
        },
      ].filter(
        m => m.variant === (page === 'inicio' ? 'inicio' : 'resultados')
      ),
    [page, showCurules, t]
  );

  useEffect(() => {
    if (!page) return;
    if (page !== selectedMap?.variant) {
      setSelectedMap(maps[0]);
      setTooltipProps({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maps, page, selectedMap?.variant]);

  return (
    <MapContext.Provider
      value={{
        mapRef,
        mapDomRef,
        tooltipRef,
        mapMeasure,
        maps,
        selectedMap,
        setSelectedMap,
        tooltipVisible,
        setTooltipVisible,
        tooltipProps,
        setTooltipProps,
      }}
    >
      {children}
    </MapContext.Provider>
  );
};

export default MapContext;

export const useMap = () => useContext(MapContext);
